/**
 * LanguageSwitcher — single unified i18n language picker for the entire app.
 *
 * Replaces the previously-separate `LanguageSelector` component. One control,
 * three visual variants, one set of data-testids:
 *
 *   data-testid="language-switcher"            (root container)
 *   data-testid="language-switcher-trigger"    (the clickable button / select)
 *   data-testid="language-switcher-option-en"  (per-language option)
 *
 * Variants:
 *   - "compact" → tiny inline select used in sidebars (StudentPortal,
 *     ParentPortal). Persists via i18next-browser-languagedetector.
 *   - "ghost"   → small ghost button + dropdown menu used in floating
 *     positions (event landing top-right, admin header). Shows the flag.
 *   - "default" → full button + dropdown with native name + flag. Used in
 *     prominent navs (events landing nav, scanner pages).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { setGoogleTranslateLanguage } from '@/lib/googleTranslate';

const LanguageSwitcher = ({ variant = 'compact', showLabel = true, align = 'end' }) => {
  const { i18n, t } = useTranslation();
  const currentCode = i18n.resolvedLanguage || i18n.language || 'en';
  const current = SUPPORTED_LANGUAGES.find(l => l.code === currentCode) || SUPPORTED_LANGUAGES[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('i18nextLng', code); } catch (_e) { /* storage blocked */ }
    // Also drive Google Translate so dynamic content (exam questions,
    // modules, course material) translates alongside our static UI strings.
    setGoogleTranslateLanguage(code);
  };

  // -------- compact variant (sidebar) --------
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-1" data-testid="language-switcher">
        <Globe className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <Select value={currentCode} onValueChange={change}>
          <SelectTrigger
            className="h-7 text-xs border-0 bg-transparent px-1.5 focus:ring-0 shadow-none"
            data-testid="language-switcher-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_LANGUAGES.map((l) => (
              <SelectItem
                key={l.code}
                value={l.code}
                data-testid={`language-switcher-option-${l.code}`}
              >
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // -------- ghost / default variant (dropdown) --------
  const isGhost = variant === 'ghost' || variant === 'minimal';
  const triggerBtn = isGhost ? (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 px-2"
      data-testid="language-switcher-trigger"
    >
      <Globe className="h-4 w-4" />
      {showLabel && <span className="ml-1 text-lg">{current.flag}</span>}
    </Button>
  ) : (
    <Button
      variant="outline"
      size="sm"
      className="gap-2"
      data-testid="language-switcher-trigger"
    >
      <Globe className="h-4 w-4" />
      <span className="text-lg">{current.flag}</span>
      {showLabel && <span className="hidden sm:inline">{current.nativeName}</span>}
    </Button>
  );

  return (
    <div data-testid="language-switcher">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{triggerBtn}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-56 max-h-[350px] overflow-y-auto">
          {!isGhost && (
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
              {t('common.selectLanguage', 'Select language')}
            </div>
          )}
          {SUPPORTED_LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => change(lang.code)}
              className={`flex items-center justify-between cursor-pointer ${
                currentCode === lang.code ? 'bg-primary/10' : ''
              }`}
              data-testid={`language-switcher-option-${lang.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <div className="flex flex-col">
                  <span className="font-medium">{lang.nativeName}</span>
                  <span className="text-xs text-muted-foreground">{lang.name}</span>
                </div>
              </span>
              {currentCode === lang.code && (
                <Check className="h-4 w-4 text-green-500 ml-2" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Named + default exports so existing imports of either style keep working.
export { LanguageSwitcher };
export default LanguageSwitcher;
